import { getApiClient } from './client'
import type {
  TaggingResponseDto,
  TaggingListResponseDto,
  CreateTaggingDto,
  UpdateTaggingDto,
  EntityTagsResponseDto,
  ApplyTagsToEntityDto,
  RemoveTagFromEntityDto,
} from './generated/api'

/**
 * Taggings API Facade
 *
 * Type-safe wrapper around the generated API client for taggings operations.
 * Provides consistent error handling and cleaner API for React Query hooks.
 */

export type {
  TaggingResponseDto,
  TaggingListResponseDto,
  CreateTaggingDto,
  UpdateTaggingDto,
  EntityTagsResponseDto,
  ApplyTagsToEntityDto,
  RemoveTagFromEntityDto,
}

/**
 * Fetch all taggings with optional filters
 *
 * @param params - Query parameters for filtering
 * @param params.tagId - Filter by specific tag ID
 * @param params.taggableType - Filter by entity type (move or series)
 * @param params.taggableId - Filter by specific entity ID
 * @param params.includeRelations - Include related data (tag details)
 * @param params.limit - Maximum number of results (default: 20)
 * @param params.offset - Number of results to skip (default: 0)
 * @returns Promise resolving to list of taggings with pagination metadata
 */
export async function fetchTaggings(params?: {
  tagId?: string
  taggableType?: 'move' | 'series'
  taggableId?: string
  includeRelations?: boolean
  limit?: number
  offset?: number
}): Promise<TaggingListResponseDto> {
  const response = await getApiClient().api.taggingsControllerFindAll({
    tagId: params?.tagId,
    taggableType: params?.taggableType,
    taggableId: params?.taggableId,
    includeRelations: params?.includeRelations,
    limit: params?.limit,
    offset: params?.offset,
  })

  return response.data
}

/**
 * Fetch a single tagging by ID
 *
 * @param id - Tagging ID (UUID)
 * @returns Promise resolving to tagging details
 */
export async function fetchTagging(id: string): Promise<TaggingResponseDto> {
  const response = await getApiClient().api.taggingsControllerFindById(id)

  return response.data
}

/**
 * Get all tags for a specific entity
 *
 * @param entityType - Type of entity (move or series)
 * @param entityId - Entity ID (UUID)
 * @returns Promise resolving to entity tags
 */
export async function fetchEntityTags(
  entityType: 'move' | 'series',
  entityId: string
): Promise<EntityTagsResponseDto> {
  const response = await getApiClient().api.taggingsControllerGetEntityTags(
    entityType,
    entityId
  )

  return response.data
}

/**
 * Create a new tagging relationship
 *
 * @param data - Tagging data to create
 * @returns Promise resolving to created tagging
 */
export async function createTagging(
  data: CreateTaggingDto
): Promise<TaggingResponseDto> {
  const response = await getApiClient().api.taggingsControllerCreate(data)

  return response.data
}

/**
 * Update an existing tagging
 *
 * @param id - Tagging ID (UUID)
 * @param data - Partial tagging data to update
 * @returns Promise resolving to updated tagging
 */
export async function updateTagging(
  id: string,
  data: UpdateTaggingDto
): Promise<TaggingResponseDto> {
  const response = await getApiClient().api.taggingsControllerUpdate(id, data)

  return response.data
}

/**
 * Delete a tagging relationship
 *
 * @param id - Tagging ID (UUID)
 * @returns Promise resolving to void on success
 */
export async function deleteTagging(id: string): Promise<void> {
  await getApiClient().api.taggingsControllerDelete(id)
}

/**
 * Add multiple tags to an entity
 *
 * @param entityType - Type of entity (move or series)
 * @param entityId - Entity ID (UUID)
 * @param data - Tag IDs to apply
 * @returns Promise resolving to void on success
 */
export async function addTagsToEntity(
  entityType: 'move' | 'series',
  entityId: string,
  data: ApplyTagsToEntityDto
): Promise<void> {
  await getApiClient().api.taggingsControllerAddTagsToEntity(
    entityType,
    entityId,
    data
  )
}

/**
 * Remove a tag from an entity
 *
 * @param entityType - Type of entity (move or series)
 * @param entityId - Entity ID (UUID)
 * @param data - Tag ID to remove
 * @returns Promise resolving to void on success
 */
export async function removeTagFromEntity(
  entityType: 'move' | 'series',
  entityId: string,
  data: RemoveTagFromEntityDto
): Promise<void> {
  await getApiClient().api.taggingsControllerRemoveTagFromEntity(
    entityType,
    entityId,
    data
  )
}
